import { Mail, PhoneOff, Clock, Info, AlertTriangle, MessageSquare } from 'lucide-react';
import { formatDateTimeWithZone } from '../lib/format.js';

// WHAT THIS CALL PRODUCED — the section CallDetail didn't have.
//
// The page was 251 lines with one heading (Transcript), so everything the platform
// captures about a call was invisible unless somebody queried the database. On
// 2026-07-29 that cost a real lead: the AI captured an email, read it back
// letter-by-letter, the receptionist confirmed it — and the only place it appeared
// was prose inside `outcomeNotes`.
//
// Every field here is already written by core and was already returned by
// `GET /calls/:id` (which sends the whole Call row). Nothing new was needed to
// render it — which is the point, and the reason to check what a page ISN'T
// showing before asking for more data.
//
// ⚠️ NOT YET HERE, and deliberately not faked: Charlie's extraction writes its
// provenance and its CONFLICTS to `campaignContact.metadata.extracted`, keyed by
// callId — and `GET /calls/:id` does not include the linked contact. A conflict
// ("the call said a different address than the one on file") is the case his own
// code says a human must see, so it needs the contact on this response. Asked for;
// until it lands this section shows the call-side capture only, and says so rather
// than implying nothing was found.

const ctxOf = (call) =>
  call?.conversationContext && typeof call.conversationContext === 'object'
    ? call.conversationContext
    : {};

// Plain-language endings. `endReason` is already a human string from core
// (telnyxCallControl onHangup), so it's shown verbatim; hangupSource is the part
// that needs translating — 'caller' means US, which reads backwards to everyone.
const HANGUP_SOURCE = {
  caller: 'Alfred ended the call',
  callee: 'They hung up'
};

export default function CallCaptured({ call }) {
  const ctx = ctxOf(call);

  const email = ctx.emailCaptured || null;
  const emailAt = ctx.emailCapturedAt || null;
  const endReason = ctx.endReason || null;
  const hangupSource = ctx.hangupSource || null;
  const hangupCause = ctx.hangupCause || null;
  const subtype = call?.answeredSubtype || null;
  const openerMs = ctx.latency?.openerMs ?? null;
  // Messages the AI took on the call (realtime stack). Written by the box's take-a-message
  // tool into this same blob — so, like every other field here, nothing new was needed on
  // the server to show it.
  const messages = Array.isArray(ctx.messages) ? ctx.messages.filter(Boolean) : [];

  const hasAnything = email || endReason || subtype || hangupCause || openerMs != null
    || messages.length > 0;

  return (
    <section className="card mb-6">
      <h2 className="mb-1 text-xl text-stone-100">What this call produced</h2>
      <p className="mb-4 text-sm text-stone-400">
        Captured details and how the call ended. Anything here came off the call itself.
      </p>

      {/* ⛔ A MESSAGE IS THE MOST ACTIONABLE THING A CALL CAN PRODUCE, so it goes above
          everything, including the captured email: somebody asked to be called back and is
          waiting. It was previously written to the database and rendered NOWHERE — the
          capture worked and the delivery did not, which is worse than not having the
          feature, because the AI told the caller their message would be passed on. */}
      {messages.length > 0 && (
        <div className="mb-4 rounded-lg border border-sky-800/60 bg-sky-950/30 p-4">
          <div className="flex items-start gap-3">
            <MessageSquare size={18} className="mt-0.5 shrink-0 text-sky-300" />
            <div className="min-w-0 w-full">
              <div className="text-xs uppercase tracking-wide text-sky-400/80">
                {messages.length === 1 ? 'Message taken on this call'
                                       : `${messages.length} messages taken on this call`}
              </div>
              {messages.map((m, i) => (
                <div key={i} className={i ? 'mt-4 border-t border-sky-800/50 pt-3' : 'mt-1'}>
                  <div className="text-lg text-sky-100">{m.callerName || 'Caller'}</div>
                  {/* Selectable and monospaced: someone is going to dial this. */}
                  {m.callbackNumber && (
                    <div className="mt-0.5 select-all font-mono text-base text-sky-200">
                      {m.callbackNumber}
                    </div>
                  )}
                  {m.message && (
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-200">
                      {m.message}
                    </p>
                  )}
                  {m.takenAt && (
                    <div className="mt-1.5 text-xs text-stone-500">
                      Taken {formatDateTimeWithZone(m.takenAt)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* THE LEAD. Given first and given room — an address the AI captured is the
          most valuable thing a call can produce, and burying it in a metadata dump
          is how the 07-29 lead went unnoticed. */}
      {email ? (
        <div className="mb-4 rounded-lg border border-emerald-800/60 bg-emerald-950/30 p-4">
          <div className="flex items-start gap-3">
            <Mail size={18} className="mt-0.5 shrink-0 text-emerald-300" />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-emerald-400/80">
                Email captured on this call
              </div>
              {/* Selectable and monospaced: someone is going to copy this. */}
              <div className="mt-0.5 select-all break-all font-mono text-lg text-emerald-100">
                {email}
              </div>
              {emailAt && (
                <div className="mt-1 text-xs text-stone-400">
                  {formatDateTimeWithZone(emailAt)}
                </div>
              )}
              <p className="mt-2 text-xs text-stone-400">
                Captured, not sent. Sending the follow-up is still a manual step.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {!hasAnything ? (
        <p className="text-sm text-stone-500">
          Nothing was captured on this call, and no ending was recorded for it.
        </p>
      ) : (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(endReason || hangupSource) && (
            <div>
              <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-stone-500">
                <PhoneOff size={12} /> How it ended
              </dt>
              <dd className="mt-0.5 text-sm text-stone-200">
                {endReason || '—'}
                {hangupSource && HANGUP_SOURCE[hangupSource] && (
                  <span className="block text-xs text-stone-400">
                    {HANGUP_SOURCE[hangupSource]}
                  </span>
                )}
                {hangupCause && (
                  <span className="block font-mono text-xs text-stone-500">{hangupCause}</span>
                )}
              </dd>
            </div>
          )}

          {subtype && (
            <div>
              <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-stone-500">
                <Info size={12} /> Who answered
              </dt>
              {/* The STORED classification, not a client-side guess at the transcript. */}
              <dd className="mt-0.5 text-sm text-stone-200">{subtype.replace(/_/g, ' ')}</dd>
            </div>
          )}

          {openerMs != null && (
            <div>
              <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-stone-500">
                <Clock size={12} /> Time to first word
              </dt>
              <dd className="mt-0.5 text-sm text-stone-200">
                {(Number(openerMs) / 1000).toFixed(1)}s
                {Number(openerMs) > 3000 && (
                  <span className="ml-2 text-xs text-amber-300">slow — people hang up on silence</span>
                )}
              </dd>
            </div>
          )}
        </dl>
      )}

      {/* Honest about the seam rather than silent about it. Without this line an
          empty section reads as "the call captured nothing", which is a different
          claim from "this screen can't see the extraction yet". */}
      {call?.kind === 'campaign' && !email && (
        <p className="mt-4 flex items-start gap-2 text-xs text-stone-500">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            Names and addresses pulled out by the transcript analyser are stored on the contact, not
            on the call, so they don&rsquo;t appear here yet — open the contact in the campaign to
            see them.
          </span>
        </p>
      )}
    </section>
  );
}
